import { AfterViewInit, Directive, OnDestroy, OnInit, Type, inject } from "@angular/core";
import { MatDialog, MatDialogConfig } from "@angular/material/dialog";
import { NavigationEnd, Router } from "@angular/router";
import { Subscription, filter } from "rxjs";

import { IncidenciaIsoComun, OpcionIso, ServicioIncidenciasIso, etiquetaOpcion } from "./incidencias-iso.comun";
import { ColumnaTablero, TableroIncidenciasBase } from "./tablero-incidencias-base";

/** Lo que cambia entre el tablero de incidencias IA y el de seguridad. */
export interface ConfigTableroIso {
  /** Ruta del tablero: al volver a ella se recarga desde la primera página. */
  ruta: string;
  /** Prefijo de las clases de los diálogos: <prefijo>-dialog, <prefijo>-detalle-dialog y <prefijo>-asignacion-dialog. */
  prefijoDialogo: string;
  /** Aviso cuando falla la consulta de una columna. */
  errorConsulta: string;
  componenteDetalle: Type<unknown>;
  componenteAsignacion: Type<unknown>;
}

/**
 * Tableros de incidencias IA y de seguridad: carga inicial (también al volver a la ruta), consulta de
 * cada columna, filtros, etiquetas de las tarjetas y el detalle con lo que se abre desde él
 * (formulario o asignación de responsable). Cada pantalla da su servicio, sus textos y sus diálogos.
 */
@Directive()
export abstract class GestionIncidenciasIsoBase<TColumna extends { estado: string }, TItem extends IncidenciaIsoComun>
  extends TableroIncidenciasBase<TColumna, TItem>
  implements OnInit, AfterViewInit, OnDestroy
{
  abstract readonly gravedadOptions: OpcionIso[];
  abstract readonly prioridadOptions: OpcionIso[];

  protected readonly dialog = inject(MatDialog);
  protected abstract readonly incidenciasService: ServicioIncidenciasIso<TItem>;
  protected abstract readonly config: ConfigTableroIso;
  private readonly router = inject(Router);
  private routeSub?: Subscription;
  private cargaInicialTimer?: ReturnType<typeof setTimeout>;

  ngOnInit(): void {
    this.cargarInicio();
    this.programarCargaInicial(0);
    this.routeSub = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => {
        if (event.urlAfterRedirects.includes(this.config.ruta)) {
          this.programarCargaInicial(75);
        }
      });
  }

  ngAfterViewInit(): void {
    this.programarCargaInicial(150);
  }

  ngOnDestroy(): void {
    this.routeSub?.unsubscribe();
    if (this.cargaInicialTimer) {
      clearTimeout(this.cargaInicialTimer);
    }
  }

  override refrescarTodas(): void {
    this.errorCarga = "";
    super.refrescarTodas();
  }

  trackByIncidencia(_index: number, incidencia: TItem): number {
    return incidencia.idIncidenciaIa ?? 0;
  }

  abrirDetalle(incidencia: TItem): void {
    if (!incidencia.idIncidenciaIa) {
      return;
    }
    const ref = this.dialog.open(
      this.config.componenteDetalle,
      this.dialogoModulo({ idIncidenciaIa: incidencia.idIncidenciaIa, incidencia }, "detalle"),
    );
    ref.afterClosed().subscribe((result) => {
      this.refrescarTodas();
      if (result?.action === "edicion" || result?.action === "seguimiento") {
        this.abrirFormularioDesdeDetalle(result.action, result.incidencia);
      }
      if (result?.action === "asignacion") {
        this.abrirAsignacionDesdeDetalle(result.incidencia, result.idUsuarioSesion);
      }
    });
  }

  getActiveFiltersCount(): number {
    return [
      this.filtros.idIncidenciaIa,
      this.filtros.fechaAperturaInicio,
      this.filtros.fechaAperturaFin,
      this.filtros.notificadoPor,
      this.filtros.gravedad,
      this.filtros.prioridad,
    ].filter(Boolean).length;
  }

  labelGravedad(value?: string): string {
    return etiquetaOpcion(value, this.gravedadOptions);
  }

  labelPrioridad(value?: string): string {
    return etiquetaOpcion(value, this.prioridadOptions);
  }

  consultarColumna(column: ColumnaTablero<TColumna, TItem>): void {
    column.loading = true;
    const filtro: TItem = {
      ...this.filtros,
      estadoEvento: column.estado,
    };
    const regInicial = (column.currentPage - 1) * this.itemsPerPage;
    this.incidenciasService.consultarKanban(filtro, regInicial, this.itemsPerPage).subscribe({
      next: (res) => {
        if (!res || res.codigoError) {
          this.errorCarga = res?.mensajeError || this.config.errorConsulta;
          column.items = [];
          column.totalItems = 0;
          column.totalPages = 0;
          return;
        }
        column.items = res.incidencias || [];
        column.totalItems = Number(res.totalItems || 0);
        column.totalPages = column.totalItems > 0 ? Math.ceil(column.totalItems / this.itemsPerPage) : 0;
        if (column.totalPages > 0 && column.currentPage > column.totalPages) {
          column.currentPage = column.totalPages;
          this.consultarColumna(column);
        }
      },
      error: (e) => {
        this.errorCarga = this.mensajeErrorConsulta(e);
        column.items = [];
        column.totalItems = 0;
        column.totalPages = 0;
      },
      complete: () => {
        column.loading = false;
      },
    });
  }

  /** Primera carga del tablero al abrir la pantalla. */
  protected cargarInicio(): void {
    this.refrescarTodas();
  }

  /** Diálogo de 80vw x 75vh con las clases del módulo y, si se indica, la de su tipo (<prefijo>-<tipo>-dialog). */
  protected dialogoModulo<D>(data: D, tipo?: string): MatDialogConfig<D> {
    const prefijo = this.config.prefijoDialogo;
    return {
      disableClose: false,
      autoFocus: true,
      width: "80vw",
      height: "75vh",
      maxWidth: "80vw",
      maxHeight: "75vh",
      panelClass: ["custom-modalbox", `${prefijo}-dialog`, ...(tipo ? [`${prefijo}-${tipo}-dialog`] : [])],
      data,
    };
  }

  /** Aviso cuando falla la consulta de una columna: el mensaje del backend o el de la pantalla. */
  protected mensajeErrorConsulta(e: any): string {
    return e?.error?.mensajeError || e?.error?.message || this.config.errorConsulta;
  }

  /** Abre el formulario (edición o seguimiento) que se pidió desde el detalle. */
  protected abstract abrirFormularioDesdeDetalle(action: "edicion" | "seguimiento", incidencia: TItem): void;

  private abrirAsignacionDesdeDetalle(incidencia: TItem, idUsuarioSesion?: number): void {
    if (!incidencia?.idIncidenciaIa) {
      return;
    }
    const ref = this.dialog.open(this.config.componenteAsignacion, this.dialogoModulo({ incidencia, idUsuarioSesion }, "asignacion"));
    ref.afterClosed().subscribe(() => this.refrescarTodas());
  }

  private programarCargaInicial(delay: number): void {
    if (this.cargaInicialTimer) {
      clearTimeout(this.cargaInicialTimer);
    }
    this.cargaInicialTimer = setTimeout(() => {
      this.reiniciarPaginas();
      this.refrescarTodas();
    }, delay);
  }
}
